import React, { useState } from 'react';

const BookkeepingServices = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: 'I need help with Bookkeeping Services.',
    website_hp: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch("https://akiradvisory.in/send_mail.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      setFormStatus('success');
      // Reset form while keeping the default message
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: 'I need help with Bookkeeping Services.',
        website_hp: ''
      });
    } else {
      setFormStatus('error');
    }
  } catch (error) {
    setFormStatus('error');
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setFormStatus(''), 5000);
  }
};

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-blue-700 pt-16 pb-12">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:w-2/3">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Bookkeeping Services
            </h1>

            <p className="mt-4 text-xl text-blue-100">
              Professional bookkeeping services to maintain accurate financial records, ensure compliance, and provide clear insights into your business performance. We handle all your accounting needs with precision and expertise.
            </p>
            <div className="mt-8">
              <a href="#contact-section" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                  <span className="mr-2">📚</span> Get Accurate Financial Records
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex space-x-6 overflow-x-auto text-sm font-medium">
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#details">Overview & Benefits</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#procedure">Our Process</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#documents">Documents Required</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#you-get">What You Get</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#service-faqs">FAQs</a></li>
            </ul>
        </div>
      </section>

      {/* Overview Section */}
      <section id="details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">
                Overview of Bookkeeping Services
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Professional bookkeeping is the foundation of sound financial management for any business. It involves systematically recording all financial transactions, maintaining accurate accounts, and ensuring compliance with accounting standards.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our comprehensive bookkeeping services cover daily transaction recording, bank reconciliation, accounts payable/receivable management, and financial statement preparation. We ensure your books are always accurate and up-to-date.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With expertise in various accounting software and deep knowledge of Indian accounting standards, we provide reliable bookkeeping that forms the basis for informed business decisions and regulatory compliance.
              </p>

              <div className="mt-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                  Key Features & Benefits
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Accurate Financial Records:</strong> Maintain precise and up-to-date books of accounts with proper classification and documentation.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Timely Compliance:</strong> Ensure all financial records are compliant with Indian accounting standards and tax regulations.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Cash Flow Management:</strong> Track income and expenses accurately to maintain healthy cash flow visibility.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">GST Compliance Support:</strong> Proper recording of GST transactions and preparation for GST returns.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Bank Reconciliation:</strong> Regular bank statement reconciliation to identify discrepancies and prevent fraud.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Financial Reporting:</strong> Generate monthly financial statements including profit & loss and balance sheet.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Audit Ready Records:</strong> Maintain books that are always ready for internal or statutory audit requirements.
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-1" id="contact-section">
              <div className="bg-gray-50 p-6 rounded-xl shadow-xl border-t-4 border-blue-600 sticky top-28">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Quick Enquiry
                </h3>
                
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-100 text-green-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">✅</span>
                    Thank you! Your enquiry has been submitted. We'll contact you shortly.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">❌</span>
                    There was an error submitting your form. Please try again.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleFormSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Full Name" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <textarea 
                    rows="3" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="I need help with Bookkeeping Services." 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3 inline-block"></div>
                        Submitting...
                      </>
                    ) : (
                      'Request an Initial Consultation'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Procedure Section */}
      <section id="procedure" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Our Comprehensive Process
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              8 Steps for Professional Bookkeeping
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 grid-flow-row-dense">
            {[
              { number: "01", icon: "📁", title: "Document Collection", desc: "Gather all financial documents including invoices, receipts, bank statements, and payment vouchers." },
              { number: "02", icon: "📚", title: "Chart of Accounts Setup", desc: "Create or optimize chart of accounts tailored to your business structure and industry requirements." },
              { number: "03", icon: "📝", title: "Transaction Recording", desc: "Systematically record all sales, purchases, expenses, and other financial transactions." },
              { number: "04", icon: "💳", title: "Bank Reconciliation", desc: "Reconcile bank statements with book records to ensure accuracy and identify discrepancies." },
              { number: "05", icon: "👥", title: "Accounts Management", desc: "Manage accounts receivable and payable, track customer payments and vendor dues." },
              { number: "06", icon: "🧮", title: "GST Compliance", desc: "Record GST transactions accurately and prepare data for GST return filing." },
              { number: "07", icon: "📊", title: "Financial Reporting", desc: "Generate monthly financial statements including P&L, balance sheet, and cash flow statements." },
              { number: "08", icon: "🏆", title: "Review & Delivery", desc: "Final review of books, delivery of financial reports, and consultation on findings." }
            ].map((step, index) => (
              <div key={index} className="sm:col-span-2 lg:col-span-2 flex flex-col justify-between p-3 bg-white rounded-xl shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-blue-500">
                <div className="flex flex-col flex-grow justify-center">
                  <span className="text-xl font-extrabold text-blue-600 block leading-tight">{step.number}</span>
                  <span className="text-2xl mx-auto block leading-none">{step.icon}</span>
                  <h3 className="text-base font-semibold text-gray-900 leading-snug mt-0.5">{step.title}</h3>
                  <p className="text-gray-600 text-sm text-justify mt-1 leading-snug">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Only Scanned Copies are required
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Mandatory Documents for Bookkeeping
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "🧾", title: "Sales Invoices", desc: "All sales bills and invoices issued to customers with GST details if applicable." },
              { icon: "🛒", title: "Purchase Bills", desc: "All purchase invoices and bills received from suppliers and vendors." },
              { icon: "💳", title: "Bank Statements", desc: "Monthly bank statements for all business accounts for reconciliation." },
              { icon: "📄", title: "Expense Vouchers", desc: "All expense receipts, payment vouchers, and supporting documents." },
              { icon: "👥", title: "Payroll Records", desc: "Salary records, attendance sheets, and statutory deduction details." },
              { icon: "🚚", title: "Inventory Records", desc: "Stock registers, purchase orders, and inventory movement details." },
              { icon: "🏠", title: "Asset Records", desc: "Fixed asset purchase bills, depreciation schedules, and capital expenditure details." },
              { icon: "✅", title: "Previous Records", desc: "Previous accounting records, trial balance, and opening balances." },
              { icon: "✉️", title: "Authorization", desc: "Authorization letter for accessing bank statements and financial information." }
            ].map((doc, index) => (
              <div key={index} className="p-6 border rounded-xl shadow-md flex items-start space-x-4 h-full">
                <span className="text-2xl flex-shrink-0 mt-1">{doc.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                  <p className="text-gray-600 text-sm mt-1 text-justify">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="you-get" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Your Deliverables
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Akir Advisory Delivers
            </p>
          </div>

          {/* 5 equal boxes in a single row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: "📚", title: "Accurate Books of Accounts", desc: "Complete and accurate books maintained with proper classification and documentation as per accounting standards." },
              { icon: "📊", title: "Monthly Financial Reports", desc: "Monthly profit & loss statements, balance sheets, and cash flow statements for business insights." },
              { icon: "💳", title: "Bank Reconciliation", desc: "Monthly bank reconciliation statements ensuring all transactions are properly recorded and matched." },
              { icon: "📝", title: "GST Ready Data", desc: "Properly recorded GST transactions with purchase and sales registers ready for GST return filing." },
              { icon: "🎧", title: "Ongoing Support", desc: "Monthly bookkeeping updates, accounting software support, financial query resolution, audit preparation assistance" }
            ].map((item, index) => (
              <div key={index} className="p-5 bg-white rounded-xl shadow-lg border-l-4 border-green-500 flex flex-col h-full">
                <span className="text-2xl mb-2 block">{item.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-snug">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="service-faqs" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                  <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
                      Common Questions
                  </h2>
                  <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                      Bookkeeping Services FAQs
                  </p>
              </div>
              
              <div className="space-y-4 max-w-4xl mx-auto">
                {[
                  { 
                    question: "Why is professional bookkeeping important for my business?", 
                    answer: "Professional bookkeeping ensures accurate financial records, helps in tax compliance, provides business insights, supports decision-making, and prepares your business for audits and funding requirements." 
                  },
                  { 
                    question: "How often should bookkeeping be done?", 
                    answer: "Bookkeeping should be done regularly - ideally daily or weekly for transaction recording, with monthly closures for financial reporting. Regular bookkeeping prevents backlog and ensures accuracy." 
                  },
                  { 
                    question: "What accounting software do you use?", 
                    answer: "We work with all major accounting software including Tally, QuickBooks, Zoho Books, and Busy. We can also recommend and implement the best software for your business needs." 
                  },
                  { 
                    question: "Can you handle GST-compliant bookkeeping?", 
                    answer: "Yes, we specialize in GST-compliant bookkeeping with proper HSN/SAC code classification, input tax credit tracking, and preparation of data for GST return filing." 
                  },
                  { 
                    question: "What is the difference between bookkeeping and accounting?", 
                    answer: "Bookkeeping involves recording daily transactions, while accounting includes interpreting, classifying, analyzing, and reporting financial data. Bookkeeping is the foundation for accounting." 
                  },
                  { 
                    question: "How do you ensure data security and confidentiality?", 
                    answer: "We maintain strict confidentiality agreements, use secure data transfer methods, and follow robust data protection protocols to ensure your financial information remains secure." 
                  }
                ].map((faq, index) => (
                  <div key={index} className="border rounded-lg p-5 shadow-sm">
                    <h4 className="font-semibold text-lg text-gray-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
          </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-blue-700/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white">
                Ready for Professional Bookkeeping?
            </h2>
            <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
                Let our team of bookkeeping experts maintain accurate financial records and ensure compliance for your business.
            </p>
            <a href="#contact-section" className="mt-8 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                <span className="mr-2">📞</span> Consult Our Bookkeeping Expert Today
            </a>
        </div>
      </section>
    </main>
  );
};

export default BookkeepingServices;